import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import { SerialPort } from "src/typings";

@Injectable({
  providedIn: 'root',
})
export class SerialServiceService {
  private port: SerialPort | null = null;
  private reader: ReadableStreamDefaultReader<string> | null = null;
  private isConnecting = false; // Track if we are currently connecting
  public weight$ = new Subject<string>();

  async connect(silent = false): Promise<boolean> {
    if (this.isConnecting) {
      console.warn('Already attempting to connect. Aborting this connection attempt.');
      return false; // Prevent multiple connection attempts
    }
    this.isConnecting = true; // Set connecting flag

    const nav = navigator as any;
    if (!('serial' in nav)) {
      alert('Web Serial API not supported in this browser.');
      this.isConnecting = false; // Reset flag after attempt
      return false;
    }

    try {
      // Check for already available ports
      const ports = await nav.serial.getPorts();
      if (ports.length > 0) {
        this.port = ports[0]; // Use the first available port
        await this.port?.open({ baudRate: 9600 });

        const textDecoder = new TextDecoderStream();
        this.port?.readable.pipeTo(textDecoder.writable);
        const readableStreamClosed = textDecoder.readable;

        this.reader = readableStreamClosed.getReader();
        this.readLoop(); // Start reading data
        this.isConnecting = false; // Reset flag after successful connection
        return true; // Successfully connected
      } else if (!silent) {
        // If no ports are available, and it's not silent, ask the user to select a port
        try {
          this.port = await nav.serial.requestPort();
          await this.port?.open({ baudRate: 9600 });

          const textDecoder = new TextDecoderStream();
          this.port?.readable.pipeTo(textDecoder.writable);
          const readableStreamClosed = textDecoder.readable;

          this.reader = readableStreamClosed.getReader();
          this.readLoop(); // Start reading data
          this.isConnecting = false; // Reset flag after successful connection
          return true; // Successfully connected
        } catch (error) {
          // Handle the case when the user cancels the port selection
          if (error instanceof DOMException && error.name === 'NotFoundError') {
            console.warn('User cancelled port selection or no device selected.');
          } else {
            console.error('Error connecting to serial port:', error);
          }
          this.isConnecting = false; // Reset flag on failure
          return false; // Connection failed due to user cancellation or error
        }
      }

      this.isConnecting = false; // Reset flag if no silent connection available
      return false; // No available ports found
    } catch (error) {
      console.error('Error connecting to serial port:', error);
      this.isConnecting = false; // Reset flag on error
      return false;
    }
  }

  private async readLoop(): Promise<void> {
    try {
      while (true) {
        const { value, done } = await this.reader!.read();
        if (done) {
          break;
        }
        if (value) {
          this.weight$.next(value);
        }
      }
    } catch (error) {
      console.error('[Serial] Read error:', error);
    }
  }

  async disconnect(): Promise<void> {
    try {
      if (this.reader) {
        // Attempt to cancel reading
        await this.reader.cancel().catch((err) => {
          console.warn('Reader cancel failed:', err);
        });

        // Release the lock on the reader
        this.reader.releaseLock();
      }

      // Allow the streams to settle
      await new Promise(resolve => setTimeout(resolve, 100));

      // Now try to close the port
      if (this.port?.readable && !this.port.readable.locked) {
        await this.port.close();
      } else {
        console.warn('Port is locked, skipping close or already closed');
      }

      this.port = null; // Reset port state
      this.reader = null; // Reset reader
    } catch (err) {
      console.error('Error during serial disconnect:', err);
    }
  }

  async write(data: string): Promise<void> {
    if (!this.port?.writable) return;

    const writer = this.port.writable.getWriter();
    const encoder = new TextEncoder();
    await writer.write(encoder.encode(data));
    writer.releaseLock();
  }
}
